<template>
  <div class="app">
    <template v-if="loadingUserProfile">
      <div class="loading-container">
        <div class="app-logo-container">
          <div class="app-logo-wrapper">
            <div class="app-logo-border"></div>
            <img class="app-logo" src="https://stage-assets.autoalert.com/img/autoalert-icons/PNG/512x512/icon-autoalert-mountains-round-ocean-deep.png" />
          </div>
        </div>
        <div class="loading-text">Loading user profile</div>
        <a class="reload-app-link" href="javascript:;" @click="reloadApp()">Click here to reload</a>
      </div>
    </template>
    <template v-else-if="User && User.UserId">
      <router-view></router-view>
      <vue-snotify></vue-snotify>
      <ModalManager />
    </template>
    <div v-else class="no-user-profile-wrapper">
      <img src="https://assets.autoalert.com/img/autoalert-icons/icon-user-remove-charcoal.svg" class="no-user-profile-img" />
      <div class="no-user-profile-msg">
        <template v-if="IsImpersonatingUser && !IsImpersonationSupported">
          Impersonation detected!<br />
          Admin can't be loaded while impersonating another user.<br />
          Please dismiss impersonation or logout and login with your user
          account.<br />
          (Error code: {{ getErrorCode() }})<br /><br />
          <a class="btn btn-primary no-user-profile-btn" :href="getCXMUrl()">CXM Dashboard</a><br />
          <button type="button" class="btn btn-primary btn-outline no-user-profile-btn" @click="dismissImpersonation()">
            Dismiss Impersonation</button><br />
        </template>
        <template v-else>
          We're sorry, we couldn't find your user profile.<br />
          Please try again later or contact support for further assistance.<br />
          (Error code: {{ getErrorCode() }})<br /><br />
        </template>
        <button type="button" class="btn btn-primary btn-outline no-user-profile-btn" @click="logoutUser">
          Logout
        </button>
      </div>
    </div>
    <google-maps-modal v-show="ShowGoogleMapsModal"></google-maps-modal>
    <root-modals></root-modals>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { webAuth } from "../src/auth/index";
import { HelperFunctions } from "@/lib/utilities/HelperFunctions.js";
import router from "./router";
import ModalManager from "@/components/modals/shared/ModalManager";
import { NamedRoutes } from "@/lib/utilities/helpers/navigation";
import GoogleMapsModal from "./components/modals/GoogleMapsModal.vue";
import Scripts from "@/lib/others/Scripts.js";

export default {
  name: "app",
  components: {
    ModalManager,
    GoogleMapsModal,
    RootModals: () => import("@/components/modals/RootModals.vue"),
  },
  data() {
    return {
      loadingUserProfile: true,
    };
  },
  computed: {
    ...mapState({
      IsImpersonatingUser: (state) => state.Authentication.IsImpersonatingUser,
      IsImpersonationSupported: (state) =>
        state.Authentication.IsImpersonationSupported,
      ShowGoogleMapsModal: (state) => state.GoogleMaps.ShowGoogleMapsModal,
    }),
    ...mapGetters(["OAuthUser", "User"]),
  },
  created() {
    this.verifyLogin();
  },
  methods: {
    getProductFeatures() {
      Promise.all([
        //this.$store.dispatch("checkUserAccountFeatures"), currently not needed but may be in the near future.
        this.$store.dispatch("checkViewAccountFeatures"),
      ]).finally(() => {
        setTimeout(() => {
          this.$store.commit("featureDetailsLoaded");
        }, 300);
      });
    },
    verifyLogin() {
      webAuth
        .getOAuthUser()
        .then((resp) => {
          this.handleUserSetup();
        })
        .catch((resp) => {
          console.log("Error verifying login status: ", resp);
        });
    },
    handleUserSetup: function () {
      if (!this.IsImpersonatingUser || this.IsImpersonationSupported) {
        this.$store
          .dispatch("getCurrentUser")
          .then((resp) => {
            this.getProductFeatures();
            this.checkRouteParams();
            this.$store.dispatch("setupPubNub");
            this.$store.dispatch("getDealershipUsers");
            this.$store.dispatch("getTemplateLookupEnums");
            this.loadingUserProfile = false;
            Scripts.loadScripts(this.User);
          })
          .catch((resp) => {
            this.loadingUserProfile = false;
          });
      } else {
        // Wait a few seconds to detect if impersonation has been dismissed on another Pando sub-domain
        setTimeout(() => {
          this.loadingUserProfile = false;
          location.href = process.env.PANDO_CXM_URL;
        }, 2000);
      }
    },
    reloadApp() {
      location.reload();
    },
    getErrorCode: function () {
      if (this.IsImpersonatingUser) return "0x202020";
      else if (!this.OAuthUser || this.OAuthUser.expired) return "0x101010";
      else if (!this.User || !this.User.UserId) return "0x303030";
      else return "0x808080";
    },
    dismissImpersonation() {
      return webAuth.endImpersonation();
    },
    getCXMUrl() {
      return process.env.PANDO_CXM_URL;
    },
    logoutUser: function () {
      webAuth.logout();
    },
    checkRouteParams: function () {
      // if(window.location.search != null && window.location.search.length > 0) {
      //   var urlParams = new URLSearchParams(window.location.search);
      //   if(urlParams.get('code') != null || (urlParams.get('oauth_token') != null))
      //     router.replace('socialaccounts' + window.location.search);
      // }
      if (window.location.search != null && window.location.search.length > 0) {
        console.log("Query: ", this.$route.query);
        var urlParams = new URLSearchParams(window.location.search);
        if (
          urlParams.get("code") != null ||
          urlParams.get("oauth_token") != null
        ) {
          let data = {
            Code: urlParams.get("code"), //this.$route.query.code,
            OAuthToken: urlParams.get("oauth_token"), // this.$route.query.oauth_token,
            OAuthVerifier: urlParams.get("oauth_verifier"), // this.$route.query.oauth_verifier
          };
          const vm = this;
          this.$store
            .dispatch("authorizeAccount", data)
            .then((result) => {
              if (result) {
                this.$snotify.success(
                  "Social account successfully authorized",
                  "Success"
                );
              } else {
                this.$snotify.error(
                  "There was an error connecting your social page with your store. Please try again later.",
                  "Error"
                );
              }
            })
            .catch((error) => {
              this.$snotify.error(
                "There was an error connecting your social page with your store. Please try again later.",
                "Error"
              );
              console.log(error);
            })
            .finally(() => {
              // Redirect to social page; Router redirect isnt clearing query params
              vm.$router.replace(
                NamedRoutes.DealerAdmin.children.SocialAccounts
              );
            });
        }
      }
    },
  },
};
</script>

<style lang='scss' scoped>
.app {
  height: 100%;
  min-width: 300px;

  .loading-container {
    text-align: center;
    padding: 50px;
    position: relative;
    height: 100%;

    .app-logo-container {
      text-align: center;
      padding: 50px 0px;

      .app-logo-wrapper {
        position: relative;
        display: inline-block;

        .app-logo-border {
          background: linear-gradient(
            60deg,
            #62bfff,
            #4fb1f5,
            #1f8bd5,
            #2d77ac,
            #1e4481,
            #172c55
          );
          animation: rot 1s linear infinite;
          border-style: solid;
          border-color: transparent;
          border-radius: 50%;
          border-width: 0px;
          width: 200px;
          height: 200px;
          -webkit-box-shadow: 0px 0px 5px 5px rgba(0, 0, 0, 0.18);
          -moz-box-shadow: 0px 0px 5px 5px rgba(0, 0, 0, 0.18);
          box-shadow: 0px 0px 5px 5px rgba(0, 0, 0, 0.18);
        }

        .app-logo {
          width: 190px;
          height: 190px;
          position: absolute;
          top: 5px;
          left: 5px;
        }

        @keyframes rot {
          0% {
            transform: rotate(0deg);
          }
          25% {
            transform: rotate(90deg);
          }
          50% {
            transform: rotate(180deg);
          }
          75% {
            transform: rotate(270deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      }
    }

    .loading-text {
      font-size: 25px;
    }

    .reload-app-link {
      text-decoration: none;
      position: absolute;
      bottom: 50px;
      left: 0;
      right: 0;
    }
  }

  .no-user-profile-wrapper {
    text-align: center;
    padding: 50px;

    .no-user-profile-img {
      width: 100px;
      height: 100px;
      margin: 50px 0px;
    }

    .no-user-profile-msg {
      font-size: 25px;

      .no-user-profile-btn {
        margin-bottom: 15px;
        width: 200px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue